import { useId, useState, type KeyboardEvent } from "react";

import type { CompanySummary } from "../lib/api";
import { useCompanySearch } from "../lib/queries";
import { formatSiren } from "../lib/siren";

interface Props {
  onSelect: (company: CompanySummary) => void;
  autoFocus?: boolean;
}

/** ARIA 1.2 combobox searching the public company directory by name. */
export function CompanySearch({ onSelect, autoFocus }: Props) {
  const id = useId();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const search = useCompanySearch(query);
  const results = query.trim().length >= 2 ? (search.data ?? []) : [];
  const expanded = open && query.trim().length >= 2;

  function choose(company: CompanySummary) {
    onSelect(company);
    setQuery("");
    setOpen(false);
  }

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActive((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && expanded && results[active]) {
      e.preventDefault();
      choose(results[active]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  }

  return (
    <div className="field combobox">
      <label className="field__label" htmlFor={`${id}-input`}>
        Nom de l'entreprise
      </label>
      <input
        id={`${id}-input`}
        className="input"
        type="search"
        role="combobox"
        autoComplete="off"
        autoFocus={autoFocus}
        placeholder="Ex. : Boulangerie Martin Lyon"
        aria-expanded={expanded}
        aria-controls={`${id}-list`}
        aria-autocomplete="list"
        aria-activedescendant={expanded && results[active] ? `${id}-opt-${active}` : undefined}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setActive(0);
          setOpen(true);
        }}
        onKeyDown={onKeyDown}
        onFocus={() => setOpen(true)}
        onBlur={() => setOpen(false)}
      />
      {expanded && (
        <ul className="combobox__list" id={`${id}-list`} role="listbox" aria-label="Entreprises trouvées">
          {search.isError && <li className="combobox__empty">{search.error.message}</li>}
          {!search.isError && results.length === 0 && (
            <li className="combobox__empty">{search.isFetching ? "Recherche en cours" : "Aucune entreprise trouvée."}</li>
          )}
          {results.map((company, i) => (
            <li
              key={company.siren}
              id={`${id}-opt-${i}`}
              role="option"
              aria-selected={i === active}
              className="combobox__option"
              onMouseEnter={() => setActive(i)}
              // mousedown fires before the input blur closes the list
              onMouseDown={(e) => {
                e.preventDefault();
                choose(company);
              }}
            >
              <span className="combobox__option-name">{company.name}</span>
              <span className="combobox__option-siren">{formatSiren(company.siren)}</span>
              <span className="combobox__option-meta">
                {[company.postal_code, company.city].filter(Boolean).join(" ")}
                {company.active === false && ", entreprise cessée"}
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
